import { FunctionComponent, memo, useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { UserContext } from '../../../context';
import { getDeepLinkUrl } from '../../utils';
import { Button, ButtonType } from '../Button';
import { IconLabel } from '../IconLabel';

export enum DeeplinkType {
  CHANNEL = 'CHANNEL',
  STORY = 'STORY',
  USER = 'USER',
  CARD = 'CARD',
  DRAFT = 'DRAFT',
  CHAT = 'CHAT',
  REFERRAL = 'REFERRAL',
  EVENT = 'EVENT',
  EVENT_DRAFT = 'EVENT_DRAFT',
  BOOKMARKS = 'BOOKMARKS',
  EVENTS = 'EVENTS',
  MY_TOPICS = 'MY_TOPICS',
  PUSH_NOTIFICATIONS = 'PUSH_NOTIFICATIONS',
  NEWSLETTERS = 'NEWSLETTERS',
  USER_PROFILE = 'USER_PROFILE',
  LEADERBOARD = 'LEADERBOARD',
}

interface DeeplinkProps {
  type: DeeplinkType;
  userId?: number;
  storyId?: number;
  cardId?: number;
  chatId?: number;
  eventId?: number;
  label?: string;
  className?: string;
  onClick?: () => void;
  showAsButton?: boolean;
}

export const Deeplink: FunctionComponent<DeeplinkProps> = memo(
  ({ type, userId, storyId, cardId, chatId, eventId, label, className, onClick, showAsButton }) => {
    const { t } = useTranslation();

    const { channelId } = useContext(UserContext).userInfo.userData;

    const isDraft = useMemo(
      () => [DeeplinkType.DRAFT, DeeplinkType.EVENT_DRAFT].includes(type),
      [type]
    );

    const copyLocaliseKey = useMemo(() => {
      if (type === DeeplinkType.DRAFT) {
        return 'cardActions.preview-link-copied';
      }

      if (type === DeeplinkType.EVENT_DRAFT) {
        return 'eventActions.preview-link-copied';
      }

      return 'deepLink.copied';
    }, [type]);

    const copyDeeplinkHandler = useCallback(async () => {
      onClick?.();

      if (!navigator.clipboard) {
        return;
      }

      await navigator.clipboard.writeText(
        getDeepLinkUrl({ type, channelId, userId, storyId, cardId, chatId, eventId })
      );

      setTimeout(() => toast(t(copyLocaliseKey)), 0);
    }, [cardId, channelId, chatId, copyLocaliseKey, eventId, onClick, storyId, t, type, userId]);

    if (showAsButton) {
      return (
        <Button
          type={ButtonType.secondary}
          label={label ?? t('deepLink.label')}
          className={className}
          onClick={copyDeeplinkHandler}
          fullWidth
        />
      );
    }

    return (
      <IconLabel
        iconId={isDraft ? 'preview-link' : 'add-link'}
        iconSize={18}
        label={label ?? t('deepLink.label')}
        className={className}
        onClick={copyDeeplinkHandler}
        singleColor
      />
    );
  }
);
